import type {
  DateTimePartsFragment,
  PageInfoPartsFragment,
  PlainSDKError,
  ThreadStatus,
} from '@team-plain/typescript-sdk'

// Util types not exported from Plain. We need these to reconstruct their response
// types on the client side, since we don't have e2e type safety through api routes.
type Data<T> = {
  data: T
  error?: never
}
type Err<U> = {
  data?: never
  error: U
}
type Result<T, U> = NonNullable<Data<T> | Err<U>>

export type SDKResult<T> = Result<T, PlainSDKError>

export type Thread = {
  id: string
  title: string
  status: 'OPEN' | 'CLOSED'
  creatorName: string
  priority: ThreadPriority
  createdAt: string
  updatedAt: string
  statusChangedAt: string
  statusChangedBy: Actor
  messageCount: number
}

export enum ThreadPriority {
  Low = 0,
  Normal = 1,
  High = 2,
  Urgent = 3,
}

export type ThreadsResponse = {
  threads: Thread[]
}

// Types for the timeline
export type Actor =
  | {
      __typename: 'UserActor'
      user: {
        fullName: string
        avatarUrl: string
      }
    }
  | {
      __typename: 'CustomerActor'
      customer: {
        fullName: string
        avatarUrl: string
      }
    }
  | {
      __typename: 'DeletedCustomerActor'
    }
  | {
      __typename: 'SystemActor'
    }
  | {
      __typename: 'MachineUserActor'
      machineUser: {
        fullName: string
      }
    }

export type ComponentText = {
  __typename: 'ComponentText'
  text: string
}

export type CustomTimelineEntryComponent = ComponentText

export type CustomEntry = {
  __typename: 'CustomEntry'
  title: string
  components: [CustomTimelineEntryComponent]
}

export type Customer = {
  fullName: string
}

export type ChatEntry = {
  __typename: 'ChatEntry'
  chatId: string
  text: string
}

export type EmailParticipant = {
  email: string
  name: string | null
}

export type EmailEntry = {
  __typename: 'EmailEntry'
  to: EmailParticipant
  from: EmailParticipant
  additionalRecipients: EmailParticipant[]
  hiddenRecipients: EmailParticipant[]
  subject: string | null
  textContent: string | null
  markdownContent: string | null
}

export type SlackMessageEntry = {
  __typename: 'SlackMessageEntry'
}

export type SlackReplyEntry = {
  __typename: 'SlackReplyEntry'
}

export type ThreadStatusTransitionedEntry = {
  __typename: 'ThreadStatusTransitionedEntry'
  previousStatus: ThreadStatus
  nextStatus: ThreadStatus
}

export type TimelineEntry =
  | ChatEntry
  | CustomEntry
  | EmailEntry
  | SlackMessageEntry
  | SlackReplyEntry
  | ThreadStatusTransitionedEntry

export type TimelineEntries = {
  cursor: string
  node: {
    id: string
    timestamp: DateTimePartsFragment
    actor: Actor
    entry: TimelineEntry
  }
}[]

export type ThreadTimeline = {
  tenant: {
    externalId: string
  }
  title: string
  description: string
  priority: ThreadPriority
  status: ThreadStatus
  createdAt: DateTimePartsFragment
  customer: Customer
  updatedAt: DateTimePartsFragment
  timelineEntries: {
    edges: TimelineEntries
  }
  pageInfo: PageInfoPartsFragment
}

export type ThreadTimelineResult = {
  thread: ThreadTimeline
}
